/**
 * Utilities for restricted-word detection and highlighting.
 *
 * Types from API:
 * - Watch: do not block send; highlight in watch color for attention.
 * - Prohibite/Probibite: block send and prevent from showing.
 * - isLink: true = block send when message contains this link (e.g. payment URLs).
 *
 * Built-in blocking (no API config): phone numbers, emails (including obfuscated forms),
 * and any URL/link (http(s), www, bare domains, IPv4) are always treated as prohibited and block send.
 *
 * @module restrictedWordsUtils
 */

// Constants
const REGEX_ESCAPE_PATTERN = /[.*+?^${}()|[\]\\]/g;
const PROTOCOL_PATTERN = /(https?:\/\/)$/i;
const WWW_PATTERN = /(www\.)$/i;
const URL_START_DELIMITERS = /[\s<>"'(]/;
const URL_VALID_CHARS = /[\w\-.:\/]/;
const URL_END_DELIMITERS = /[\s<>"')]/;
const URL_CONTINUE_CHARS = /[\w\-.:\/?#=&]/;
const MAX_PROTOCOL_LOOKBACK = 8;
const MAX_WWW_LOOKBACK = 4;

// Phone: optional + and digits, with optional separators (space, -, ., parentheses). Min 7 digits to avoid years/short numbers.
// Matches: (123) 456-7890, 123-456-7890, +1 123 456 7890, 123.456.7890, 1234567890, 123 456 7890
// At least 7 digits with optional + and separators (space, -, ., parentheses)
const PHONE_PATTERN = /(?:\+\s*)?(?:\d[\s\-\.()]*){6,}\d/g;

// Email: standard local@domain; also obfuscations: "user at domain dot com", "user [at] domain [dot] com"
const EMAIL_STANDARD = /[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/g;
const EMAIL_OBFUSCATED_PATTERN = /[\w.%+-]+\s*(?:\[?\s*at\s*\]?|\(\s*at\s*\))\s*[\w.-]+\s*(?:\[?\s*dot\s*\]?|\(\s*dot\s*\))\s*[a-zA-Z]{2,}/gi;

const MIN_PHONE_DIGITS = 7;

// Links: protocol (incl. hxxp obfuscation), www, bare domain.tld[/path], IPv4 host
const URL_PROTOCOL_REGEX = /(?:https?|hxxps?):\/\/[^\s<>"')]+/gi;
const URL_WWW_REGEX = /\bwww\.[^\s<>"')]+/gi;
const URL_BARE_DOMAIN_REGEX =
  /\b(?:[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+(?:[a-z]{2,})(?:\/[^\s<>"')]*)?/gi;
const URL_IPV4_REGEX = /\b(?:\d{1,3}\.){3}\d{1,3}(?::\d+)?(?:\/[^\s<>"')]*)?\b/g;

// Type definitions
/**
 * @typedef {Object} RestrictedWord
 * @property {string} [Keyword] - Keyword (uppercase property name)
 * @property {string} [keyword] - Keyword (lowercase property name)
 * @property {boolean|string|number} [isLink] - Whether this keyword should match URLs (boolean true, or string "true")
 * @property {boolean|string|number} [IsLink] - PascalCase variant from some APIs
 */

/**
 * @typedef {Object} TextRange
 * @property {number} start - Start position (inclusive)
 * @property {number} end - End position (exclusive)
 */

/**
 * @typedef {Object} HighlightSegment
 * @property {string} text - The text content of the segment
 * @property {boolean} restricted - Whether this segment contains restricted content
 */

/**
 * @typedef {'normal'|'watch'|'prohibit'} SegmentHighlightType
 */

/**
 * @typedef {Object} TypedHighlightSegment
 * @property {string} text - The text content of the segment
 * @property {SegmentHighlightType} type - 'normal' | 'watch' | 'prohibit'
 */

/**
 * Escapes special regex characters in a string to make it safe for use in RegExp.
 * @param {string} str - The string to escape
 * @returns {string} - The escaped string
 * @private
 */
function escapeRegex(str) {
  if (typeof str !== 'string') return '';
  return str.replace(REGEX_ESCAPE_PATTERN, '\\$&');
}

/** Single-character keywords are not blocked (e.g. stray "q" / "x"); multi-char rules like "qq" still apply. */
const MIN_RESTRICTED_KEYWORD_LENGTH = 2;

/** Minimum keyword length to use flexible spacing regex; shorter words use strict \b match to avoid false positives (e.g. "ass" in "as soon as possible"). */
const FLEXIBLE_SPACING_MIN_LENGTH = 4;

/** Minimum run length to collapse (2 = also catch "viids" → "vids", "vids" → "vids"). */
const REPEATED_CHAR_COLLAPSE_MIN = 2;

/**
 * Builds a regex that allows optional spaces or punctuation between each letter of the keyword.
 * Used to block spaced-letter bypasses like "c a s h a p p", "c-a-s-h-a-p-p", "c.a.s.h.a.p.p".
 * Only safe for keywords of length >= FLEXIBLE_SPACING_MIN_LENGTH to avoid false positives.
 *
 * @param {string} keyword - The keyword (will be escaped per character)
 * @returns {RegExp} - Pattern with \b word boundaries, case-insensitive
 * @private
 */
function createFlexibleKeywordRegex(keyword) {
  if (typeof keyword !== 'string' || keyword.length === 0) return /(?!)/;
  const letters = keyword.split('');
  const pattern = letters.map((c) => escapeRegex(c)).join('[\\s._-]*');
  return new RegExp(`\\b${pattern}\\b`, 'gi');
}

/**
 * Normalize repeated characters so stretched/evasion words are detected.
 * - fancentrooooo → fancentro (long stretches)
 * - viids → vids, many viids → many vids (so "vids" blocklist matches "viids")
 * Collapses runs of REPEATED_CHAR_COLLAPSE_MIN+ identical characters to a single character.
 * Also returns a mapping from each normalized character index to its original (start, end) span,
 * so we can map match ranges back to the original text for highlighting.
 *
 * @param {string} text - The string to normalize
 * @returns {{ normalized: string, originalSpans: Array<{ start: number, end: number }> }}
 * @private
 */
function normalizeRepeatedCharsWithMapping(text) {
  if (typeof text !== 'string') return { normalized: '', originalSpans: [] };
  const originalSpans = [];
  let normalized = '';
  let i = 0;
  while (i < text.length) {
    const start = i;
    const char = text[i];
    let j = i + 1;
    while (j < text.length && text[j].toLowerCase() === char.toLowerCase()) j++;
    const runLen = j - i;
    i = j;
    if (runLen >= REPEATED_CHAR_COLLAPSE_MIN) {
      normalized += char;
      originalSpans.push({ start, end: start + runLen });
    } else {
      for (let k = 0; k < runLen; k++) {
        normalized += char;
        originalSpans.push({ start: start + k, end: start + k + 1 });
      }
    }
  }
  return { normalized, originalSpans };
}

/**
 * Normalize repeated characters in text (e.g. looooove → love, viids → vids, fancentrooooo → fancentro).
 * Collapses runs of 2+ identical characters to a single character (aligned with matching logic).
 * Useful when you only need the normalized string (e.g. for display or other checks).
 *
 * @param {string} text - The string to normalize
 * @returns {string} - Normalized string
 */
export function normalizeRepeatedChars(text) {
  if (typeof text !== 'string') return '';
  return text.replace(/(.)\1+/gi, '$1');
}

/**
 * Gets trimmed keyword from a restricted word object (Keyword or keyword).
 * @param {RestrictedWord} word
 * @returns {string|null} - Keyword or null if missing/invalid
 * @private
 */
function getWordKeyword(word) {
  if (!word || typeof word !== 'object') return null;
  const k = (word.Keyword ?? word.keyword ?? '').trim();
  return k || null;
}

/**
 * True when this row should be treated as a link rule (block when URL/text contains the keyword).
 * Accepts boolean true, numeric 1, or string "true" (common API serialization).
 * @param {RestrictedWord} word
 * @returns {boolean}
 * @private
 */
function isLinkRestrictedEntry(word) {
  if (!word || typeof word !== 'object') return false;
  const v = word.isLink ?? word.IsLink;
  if (v === true || v === 1) return true;
  if (typeof v === 'string' && v.trim().toLowerCase() === 'true') return true;
  return false;
}

/**
 * Extracts unique keyword strings from restrictedWords array.
 * Supports both 'Keyword' and 'keyword' property names (case-insensitive deduplication).
 * 
 * @param {RestrictedWord[]} restrictedWords - Array of restricted word objects
 * @returns {string[]} - Array of unique keyword strings (preserves original case)
 * 
 * @example
 * getRestrictedKeywords([
 *   { Keyword: 'spam' },
 *   { keyword: 'test' },
 *   { keyword: 'SPAM' } // Duplicate, will be filtered
 * ])
 * // Returns: ['spam', 'test']
 */
export function getRestrictedKeywords(restrictedWords) {
  if (!Array.isArray(restrictedWords) || restrictedWords.length === 0) return [];

  const seen = new Set();
  const keywords = [];

  for (const word of restrictedWords) {
    const keyword = getWordKeyword(word);
    if (!keyword) continue;

    const lowerKey = keyword.toLowerCase();
    if (seen.has(lowerKey)) continue;

    seen.add(lowerKey);
    keywords.push(keyword);
  }

  return keywords;
}

/** Normalized type: 'Watch' | 'Prohibite' (covers Prohibite/Probibite/Prohibited/Prohibit) | '' */
function getNormalizedType(word) {
  if (!word || typeof word !== 'object') return '';
  const raw = (word.Type ?? word.type ?? '').toString().trim().toLowerCase();
  if (raw === 'watch') return 'Watch';
  if (
    raw === 'prohibite' ||
    raw === 'probibite' ||
    raw === 'prohibited' ||
    raw === 'prohibit'
  ) {
    return 'Prohibite';
  }
  return '';
}

/**
 * Returns restricted word items that are "Watch" type (highlight only, do not block send).
 * @param {RestrictedWord[]} restrictedWords
 * @returns {RestrictedWord[]}
 */
export function getWatchWords(restrictedWords) {
  if (!Array.isArray(restrictedWords)) return [];
  return restrictedWords.filter((w) => getNormalizedType(w) === 'Watch');
}

/**
 * Returns restricted word items that are "Prohibite" / "Probibite" type (block send).
 * @param {RestrictedWord[]} restrictedWords
 * @returns {RestrictedWord[]}
 */
export function getProhibitWords(restrictedWords) {
  if (!Array.isArray(restrictedWords)) return [];
  return restrictedWords.filter((w) => getNormalizedType(w) === 'Prohibite');
}

/**
 * Returns restricted word items where isLink is true (block send when message contains this link).
 * @param {RestrictedWord[]} restrictedWords
 * @returns {RestrictedWord[]}
 */
export function getLinkWords(restrictedWords) {
  if (!Array.isArray(restrictedWords)) return [];
  return restrictedWords.filter((w) => isLinkRestrictedEntry(w));
}

/**
 * Returns ranges in text that look like phone numbers (7+ digits with common separators)
 * or emails (standard or obfuscated e.g. "user at domain dot com").
 * @param {string} text
 * @returns {TextRange[]}
 */
export function getPhoneAndEmailRanges(text) {
  if (typeof text !== 'string' || !text) return [];
  const ranges = [];

  const phoneRegex = new RegExp(PHONE_PATTERN.source, 'g');
  let match;
  while ((match = phoneRegex.exec(text)) !== null) {
    const segment = match[0];
    const digitCount = (segment.match(/\d/g) || []).length;
    if (digitCount >= MIN_PHONE_DIGITS) {
      ranges.push({ start: match.index, end: match.index + segment.length });
    }
  }

  const emailStandardRegex = new RegExp(EMAIL_STANDARD.source, 'g');
  while ((match = emailStandardRegex.exec(text)) !== null) {
    ranges.push({ start: match.index, end: match.index + match[0].length });
  }

  const emailObfuscatedRegex = new RegExp(EMAIL_OBFUSCATED_PATTERN.source, 'gi');
  while ((match = emailObfuscatedRegex.exec(text)) !== null) {
    ranges.push({ start: match.index, end: match.index + match[0].length });
  }

  if (ranges.length === 0) return [];
  ranges.sort((a, b) => a.start - b.start);
  const merged = [ranges[0]];
  for (let i = 1; i < ranges.length; i++) {
    const cur = ranges[i];
    const last = merged[merged.length - 1];
    if (cur.start <= last.end) {
      last.end = Math.max(last.end, cur.end);
    } else {
      merged.push(cur);
    }
  }
  return merged;
}

/**
 * True if the text contains something that looks like a phone number or email (including obfuscated).
 * @param {string} text
 * @returns {boolean}
 */
export function hasPhoneOrEmail(text) {
  return getPhoneAndEmailRanges(text).length > 0;
}

/**
 * Merges overlapping or adjacent text ranges.
 * @param {TextRange[]} ranges
 * @returns {TextRange[]}
 * @private
 */
function mergeTextRanges(ranges) {
  if (!ranges.length) return [];
  const sorted = [...ranges].sort((a, b) => a.start - b.start);
  const merged = [{ ...sorted[0] }];
  for (let i = 1; i < sorted.length; i++) {
    const cur = sorted[i];
    const last = merged[merged.length - 1];
    if (cur.start <= last.end) {
      last.end = Math.max(last.end, cur.end);
    } else {
      merged.push({ ...cur });
    }
  }
  return merged;
}

/**
 * Returns ranges in text that look like URLs or links (any link blocks send).
 * @param {string} text
 * @returns {TextRange[]}
 */
export function getLinkRanges(text) {
  if (typeof text !== 'string' || !text) return [];
  const ranges = [];

  const patterns = [URL_PROTOCOL_REGEX, URL_WWW_REGEX, URL_BARE_DOMAIN_REGEX, URL_IPV4_REGEX];
  for (const pattern of patterns) {
    const regex = new RegExp(pattern.source, pattern.flags);
    let match;
    while ((match = regex.exec(text)) !== null) {
      ranges.push({ start: match.index, end: match.index + match[0].length });
    }
  }

  return mergeTextRanges(ranges);
}

/**
 * True if the text contains any URL or link-like pattern.
 * @param {string} text
 * @returns {boolean}
 */
export function hasLink(text) {
  return getLinkRanges(text).length > 0;
}

/**
 * True if the text contains any Prohibite-type keyword, any isLink keyword, phone/email, or any link (block sending).
 * Watch-only words do not cause this to be true.
 * @param {string} text
 * @param {RestrictedWord[]} restrictedWords
 * @returns {boolean}
 */
export function hasBlockingRestrictedContent(text, restrictedWords) {
  if (hasPhoneOrEmail(text)) return true;
  if (hasLink(text)) return true;
  if (!Array.isArray(restrictedWords) || restrictedWords.length === 0) return false;
  const blocking = [...getProhibitWords(restrictedWords), ...getLinkWords(restrictedWords)];
  return hasRestrictedWord(text, blocking);
}

/**
 * Finds the complete URL boundaries containing a keyword match.
 * Handles URLs in formats: http://example.com, https://example.com, www.example.com, example.com
 * 
 * @param {string} text - The full text content
 * @param {number} keywordStart - Start position of the keyword match
 * @param {number} keywordEnd - End position of the keyword match
 * @returns {TextRange} - Object with start and end positions of the complete URL
 * @private
 */
function findCompleteUrl(text, keywordStart, keywordEnd) {
  if (typeof text !== 'string' || keywordStart < 0 || keywordEnd > text.length) {
    return { start: keywordStart, end: keywordEnd };
  }

  let urlStart = keywordStart;

  const protocolLookbackStart = Math.max(0, keywordStart - MAX_PROTOCOL_LOOKBACK);
  const protocolMatch = text.substring(protocolLookbackStart, keywordStart).match(PROTOCOL_PATTERN);

  if (protocolMatch) {
    urlStart = keywordStart - protocolMatch[1].length;
  } else {
    const wwwLookbackStart = Math.max(0, keywordStart - MAX_WWW_LOOKBACK);
    const wwwMatch = text.substring(wwwLookbackStart, keywordStart).match(WWW_PATTERN);

    if (wwwMatch) {
      urlStart = keywordStart - wwwMatch[1].length;
    } else {
      for (let i = keywordStart - 1; i >= 0; i--) {
        const char = text[i];
        if (URL_START_DELIMITERS.test(char) || !URL_VALID_CHARS.test(char)) {
          urlStart = i + 1;
          break;
        }
      }
    }
  }

  let urlEnd = keywordEnd;
  for (let i = keywordEnd; i < text.length; i++) {
    const char = text[i];
    if (URL_END_DELIMITERS.test(char)) {
      urlEnd = i;
      break;
    }
    if (URL_CONTINUE_CHARS.test(char)) {
      urlEnd = i + 1;
    } else {
      urlEnd = i;
      break;
    }
  }

  return { start: urlStart, end: urlEnd };
}

/**
 * Finds all ranges of restricted keyword matches in the text (case-insensitive).
 * Repeated characters (e.g. fancentrooooo, maaaany) are normalized before matching so
 * stretched-word bypasses are detected. For keywords of length ≥ 4, flexible spacing is used so
 * spaced-letter bypasses (e.g. c a s h a p p, c-a-s-h-a-p-p, c.a.s.h.a.p.p) are also detected;
 * shorter keywords use strict word-boundary matching to avoid false positives.
 * Ranges are mapped back to the original text for highlighting.
 * For keywords with isLink=true, the complete URL is marked instead of just the keyword.
 * Overlapping ranges are automatically merged.
 *
 * @param {string} text - The text to search
 * @param {RestrictedWord[]} restrictedWords - Array of restricted word objects
 * @returns {TextRange[]} - Array of non-overlapping ranges sorted by start position
 *
 * @example
 * getRestrictedRanges('Visit example.com today', [
 *   { keyword: 'example.com', isLink: true }
 * ])
 * // Returns: [{ start: 6, end: 17 }]
 *
 * getRestrictedRanges('fancentrooooo', [{ keyword: 'fancentro' }])
 * // Returns: [{ start: 0, end: 14 }]
 */
export function getRestrictedRanges(text, restrictedWords) {
  if (typeof text !== 'string' || !text) return [];
  if (!Array.isArray(restrictedWords) || restrictedWords.length === 0) return [];

  const { normalized, originalSpans } = normalizeRepeatedCharsWithMapping(text);
  const ranges = [];

  for (const word of restrictedWords) {
    const keyword = getWordKeyword(word);
    if (!keyword || keyword.length < MIN_RESTRICTED_KEYWORD_LENGTH) continue;

    const isLink = isLinkRestrictedEntry(word);
    // Use normalized keyword for non-link matching so repeated-char collapse is consistent:
    // e.g. keyword "zell" and user text "zell" both normalize to "zel", so we match.
    const keywordForRegex = isLink ? keyword : normalizeRepeatedChars(keyword) || keyword;
    const regex = isLink
      ? new RegExp(escapeRegex(keyword), 'gi')
      : keywordForRegex.length < FLEXIBLE_SPACING_MIN_LENGTH
        ? new RegExp(`\\b${escapeRegex(keywordForRegex)}\\b`, 'gi')
        : createFlexibleKeywordRegex(keywordForRegex);

    if (isLink) {
      // Match on original text so URL detection is not affected by repeated-char normalization.
      const linkMatches = Array.from(text.matchAll(regex));
      for (const match of linkMatches) {
        const origStart = match.index;
        const origEnd = match.index + match[0].length;
        if (origEnd - origStart < keyword.length) continue;
        const urlRange = findCompleteUrl(text, origStart, origEnd);
        ranges.push({ start: urlRange.start, end: urlRange.end });
      }
      continue;
    }

    const matches = Array.from(normalized.matchAll(regex));
    for (const match of matches) {
      const normStart = match.index;
      const normEnd = match.index + match[0].length;
      const origStart = originalSpans[normStart].start;
      const origEnd = originalSpans[normEnd - 1].end;
      // Avoid false positives when repeated-char collapse shortens the keyword (e.g. "qq" → "q").
      if (origEnd - origStart < keyword.length) continue;
      ranges.push({ start: origStart, end: origEnd });
    }
  }

  if (ranges.length === 0) return [];

  ranges.sort((a, b) => a.start - b.start);

  const merged = [ranges[0]];
  for (let i = 1; i < ranges.length; i++) {
    const current = ranges[i];
    const last = merged[merged.length - 1];
    if (current.start <= last.end) {
      last.end = Math.max(last.end, current.end);
    } else {
      merged.push(current);
    }
  }

  return merged;
}

/**
 * Checks whether the text contains any restricted keywords.
 * 
 * @param {string} text - The text to check
 * @param {RestrictedWord[]} restrictedWords - Array of restricted word objects
 * @returns {boolean} - True if any restricted keywords are found
 * 
 * @example
 * hasRestrictedWord('Hello world', [{ keyword: 'spam' }])
 * // Returns: false
 * 
 * hasRestrictedWord('Hello spam', [{ keyword: 'spam' }])
 * // Returns: true
 */
export function hasRestrictedWord(text, restrictedWords) {
  const ranges = getRestrictedRanges(text, restrictedWords);
  return ranges.length > 0;
}

/**
 * Splits text into segments, marking restricted parts for highlighting.
 * 
 * @param {string} text - The text to segment
 * @param {RestrictedWord[]} restrictedWords - Array of restricted word objects
 * @returns {HighlightSegment[]} - Array of text segments with restriction flags
 * 
 * @example
 * getHighlightSegments('Hello spam world', [{ keyword: 'spam' }])
 * // Returns: [
 * //   { text: 'Hello ', restricted: false },
 * //   { text: 'spam', restricted: true },
 * //   { text: ' world', restricted: false }
 * // ]
 */
export function getHighlightSegments(text, restrictedWords) {
  if (typeof text !== 'string' || !text) return [];

  const ranges = getRestrictedRanges(text, restrictedWords);
  if (ranges.length === 0) return [{ text, restricted: false }];

  const segments = [];
  let currentIndex = 0;

  for (const { start, end } of ranges) {
    if (currentIndex < start) {
      segments.push({ text: text.slice(currentIndex, start), restricted: false });
    }
    segments.push({ text: text.slice(start, end), restricted: true });
    currentIndex = end;
  }

  if (currentIndex < text.length) {
    segments.push({ text: text.slice(currentIndex), restricted: false });
  }

  return segments;
}

/**
 * Merges typed ranges (start, end, type). Overlapping ranges: 'prohibit' wins over 'watch'.
 * @param {{ start: number, end: number, type: SegmentHighlightType }[]} typedRanges
 * @returns {{ start: number, end: number, type: SegmentHighlightType }[]}
 */
function mergeTypedRanges(typedRanges) {
  if (!typedRanges.length) return [];
  const sorted = [...typedRanges].sort((a, b) => a.start - b.start);
  const merged = [{ ...sorted[0] }];
  for (let i = 1; i < sorted.length; i++) {
    const cur = sorted[i];
    const last = merged[merged.length - 1];
    if (cur.start <= last.end) {
      last.end = Math.max(last.end, cur.end);
      last.type = last.type === 'prohibit' || cur.type === 'prohibit' ? 'prohibit' : last.type;
    } else {
      merged.push({ ...cur });
    }
  }
  return merged;
}

/**
 * Splits text into segments with highlight type for Watch vs Prohibit.
 * Watch words are highlighted for attention; Prohibit/link words block send and can be shown in red.
 *
 * @param {string} text - The text to segment
 * @param {RestrictedWord[]} restrictedWords - Full list from API (Type: Watch / Prohibite, isLink)
 * @returns {TypedHighlightSegment[]} - Segments with type: 'normal' | 'watch' | 'prohibit'
 */
export function getHighlightSegmentsWithTypes(text, restrictedWords) {
  if (typeof text !== 'string' || !text) return [];

  const words = restrictedWords || [];
  const watchRanges = getRestrictedRanges(text, getWatchWords(words)).map((r) => ({ ...r, type: 'watch' }));
  const prohibitRanges = getRestrictedRanges(text, [...getProhibitWords(words), ...getLinkWords(words)]).map(
    (r) => ({ ...r, type: 'prohibit' })
  );
  const phoneEmailRanges = getPhoneAndEmailRanges(text).map((r) => ({ ...r, type: 'prohibit' }));
  const linkRanges = getLinkRanges(text).map((r) => ({ ...r, type: 'prohibit' }));
  const typedRanges = mergeTypedRanges([
    ...watchRanges,
    ...prohibitRanges,
    ...phoneEmailRanges,
    ...linkRanges,
  ]);

  if (typedRanges.length === 0) return [{ text, type: 'normal' }];

  const segments = [];
  let currentIndex = 0;

  for (const { start, end, type } of typedRanges) {
    if (currentIndex < start) segments.push({ text: text.slice(currentIndex, start), type: 'normal' });
    segments.push({ text: text.slice(start, end), type });
    currentIndex = end;
  }
  if (currentIndex < text.length) segments.push({ text: text.slice(currentIndex), type: 'normal' });

  return segments;
}
